import { AppState, isPuzzlePage } from '@news-mono/web-common'
import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import {
    StyledAthena,
    StyledMain,
    StyledSidebar,
} from '../../compositions/Athena/Athena.styled'
import { ContainerWidth, ThemeMargins } from '../../__styling/settings/metrics'
import { BaseCompositionProps } from '../types'

export type SidebarOptions = 'none' | 'visibleMobile' | 'hiddenMobile'
export type ElementOptions = 'div' | 'article'

export interface AthenaProps extends BaseCompositionProps {
    mainContent: React.ReactElement<any>
    sidebarContent: React.ReactElement<any>
    verticalSpacing?: keyof ThemeMargins
    containerWidth?: ContainerWidth
    sidebarOptions: SidebarOptions
    elementType: 'div' | 'article'
    sidebarHeightLimitElementId?: string
    hasHorizontalGutters?: boolean
    hasVerticalPadding?: boolean
    hasHorizontalPadding?: boolean
    sidebarSide?: 'right' | 'left'
    hideSidebar?: boolean
}

export const Athena: React.FC<AthenaProps> = ({
    mainContent,
    sidebarContent,
    verticalSpacing,
    containerWidth,
    elementType,
    sidebarOptions = 'hiddenMobile',
    sidebarHeightLimitElementId,
    hasHorizontalGutters = true,
    hasVerticalPadding = true,
    hasHorizontalPadding,
    sidebarSide = 'right',
    hideSidebar = false,
}) => {
    const renditionType = useSelector(
        (state: AppState) => state.render.renditionType,
    )
    // We want to hide the sidebar if the user is not entitled and the hideSidebar prop is true (which is set in the article route)
    const shouldHideSideBar =
        useSelector(
            (state: AppState) => state.authentication.isEntitled === false,
        ) && hideSidebar

    const location = useLocation()

    const isPuzzlePageAppRendition =
        renditionType === 'app' && isPuzzlePage({ location })

    const [sidebarHeight, setSidebarHeight] = useState<number | undefined>(
        undefined,
    )
    const sidebarRef = React.useRef<HTMLElement | null>(null)
    const mainRef = React.useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!sidebarHeightLimitElementId) return

        const taboolaFeedWrapper = document.getElementById(
            sidebarHeightLimitElementId,
        )

        if (!taboolaFeedWrapper) return
        if (!mainRef.current) return
        if (!sidebarRef.current) return

        const resizeObserver = new ResizeObserver(() => {
            const taboolaRect = taboolaFeedWrapper.getBoundingClientRect()
            const sidebarRect = sidebarRef.current?.getBoundingClientRect()

            if (!taboolaRect || !sidebarRect) return

            const sidebarTop = sidebarRect.top
            const taboolaTop = taboolaRect.top

            // We want the height of the sidebar component to be the height
            // of the taboola feed minus the start height of the sidebar
            const sidebarHeight = taboolaTop - sidebarTop
            setSidebarHeight(sidebarHeight)
        })
        resizeObserver.observe(mainRef.current)
        return () => resizeObserver.disconnect()
    }, [mainRef, sidebarHeightLimitElementId, sidebarRef])

    return (
        <StyledAthena
            sidebarOptions={sidebarOptions}
            hideSidebar={isPuzzlePageAppRendition || shouldHideSideBar}
            verticalSpacing={verticalSpacing}
            containerWidth={containerWidth}
            hasVerticalPadding={hasVerticalPadding}
            sidebarSide={sidebarSide}
            hasHorizontalPadding={hasHorizontalPadding}
        >
            {shouldHideSideBar ||
                (sidebarOptions !== 'none' && sidebarSide === 'left' && (
                    <StyledSidebar
                        ref={sidebarRef}
                        sidebarOptions={sidebarOptions}
                        maxHeight={sidebarHeight}
                    >
                        {sidebarContent}
                    </StyledSidebar>
                ))}
            <StyledMain
                ref={mainRef}
                hasHorizontalGutters={hasHorizontalGutters}
            >
                {mainContent}
            </StyledMain>

            {shouldHideSideBar ||
                (sidebarOptions !== 'none' && sidebarSide === 'right' && (
                    <StyledSidebar
                        ref={sidebarRef}
                        sidebarOptions={sidebarOptions}
                        maxHeight={sidebarHeight}
                    >
                        {sidebarContent}
                    </StyledSidebar>
                ))}
        </StyledAthena>
    )
}
Athena.displayName = 'Athena'
